import { useEffect, useRef, useState } from 'react';

import type { EmailTemplate } from '../../pages/email-templates/api/useEmailTemplate';

import BottomSheet from './BottomSheet';
import Button from './Button';
import EtatErreur from './EtatErreur';
import Text from './Text';
import EtapeVerificationModeleEmail from './feuilleModelesEmail/EtapeVerificationModeleEmail';
import { sujetModele } from './feuilleModelesEmail/helpers';
import { t } from '../i18n/langue';

/**
 * La feuille de modèles d'e-mail — retour client 2026-09-25 : « pour les
 * e-mails, l'appli ne propose pas de types de message ». Calquée sur
 * `FeuilleModeles.tsx` (WhatsApp, #19) : liste par nom, puis relecture avant
 * envoi — un template part sans retour possible, comme un modèle WhatsApp.
 *
 * **Liste par NOM directement**, sans humanisation : contrairement aux modèles
 * WhatsApp, un modèle d'e-mail n'a pas d'identifiant technique Meta séparé du
 * nom TriggerFlow — `name` est la seule chose à afficher.
 *
 * **Pas d'étape réservation.** Un modèle d'e-mail n'a pas de `requires_reservation`
 * (`EmailTemplate`, contrairement à `WhatsappTemplate`) : le web laisse le
 * serveur résoudre la réservation (`Customer::currentOrNextOrLastReservation`)
 * quand aucune n'est explicitement fournie — voir `MessageController::sendMessage`.
 * Le mobile suit le même repli plutôt que d'imposer un choix que le web
 * lui-même ne demande pas par défaut.
 */
interface FeuilleModelesEmailProps {
    ouverte: boolean;
    onClose: () => void;
    modeles: EmailTemplate[];
    /** La locale de la cliente, pour choisir le bon sujet par défaut du modèle. */
    locale?: string | null;
    /** L'objet déjà calculé par le composeur (ex. « Re: … ») — repli si le modèle n'en a pas. */
    sujetDeRepli: string;
    onChoisir: (modele: EmailTemplate, sujet: string) => void;
    /** `modeles` charge encore : sans ce témoin, une liste vide en cours de chargement se lirait « aucun modèle ». */
    chargement?: boolean;
    /** Échec de chargement sans aucune donnée. */
    erreur?: boolean;
    onReessayer?: () => void;
}

export default function FeuilleModelesEmail({
    ouverte,
    onClose,
    modeles,
    locale,
    sujetDeRepli,
    onChoisir,
    chargement = false,
    erreur = false,
    onReessayer = () => undefined,
}: FeuilleModelesEmailProps) {
    // Audit 2026-09-23 (WhatsApp) transposé ici : un modèle part sans retour, on
    // le relit — nom et sujet exacts — avant de l'envoyer.
    const [aConfirmer, setAConfirmer] = useState<EmailTemplate | null>(null);

    const etape = aConfirmer ? 'verification' : 'liste';
    const refTitreEtape = useRef<HTMLHeadingElement>(null);
    useEffect(() => {
        if (etape !== 'liste') refTitreEtape.current?.focus();
    }, [etape]);

    const fermer = () => {
        setAConfirmer(null);
        onClose();
    };

    const sujet = aConfirmer ? sujetModele(aConfirmer, locale, sujetDeRepli) : '';

    return (
        <BottomSheet
            ouverte={ouverte}
            onClose={fermer}
            libelle={aConfirmer ? t('composeur.modelesEmail.verifier') : t('composeur.modelesEmail.choisir')}
            libelleFermeture={t('composeur.modelesEmail.fermer')}
        >
            {aConfirmer ? (
                <EtapeVerificationModeleEmail
                    modele={aConfirmer}
                    sujet={sujet}
                    titreRef={refTitreEtape}
                    onEnvoyer={() => {
                        const modele = aConfirmer;
                        setAConfirmer(null);
                        onChoisir(modele, sujet);
                    }}
                    onChoisirAutre={() => setAConfirmer(null)}
                />
            ) : (
                <>
                    <Text variante="sousTitre" as="h2" className="mb-3">
                        {t('composeur.modelesEmail.titre')}
                    </Text>

                    {chargement && modeles.length === 0 && (
                        <Text variante="petit" ton="attenue">
                            {t('commun.chargement')}
                        </Text>
                    )}

                    {erreur && modeles.length === 0 && (
                        <EtatErreur texte={t('composeur.modelesEmail.erreur')} onReessayer={onReessayer} />
                    )}

                    {!chargement && !erreur && modeles.length === 0 && (
                        <Text variante="petit" ton="attenue">
                            {t('composeur.modelesEmail.aucun')}
                        </Text>
                    )}

                    <div className="flex flex-col">
                        {modeles.map((modele) => (
                            <Button
                                key={modele.id}
                                libelle={modele.name}
                                variante="discret"
                                alignement="debut"
                                pleineLargeur
                                onPress={() => setAConfirmer(modele)}
                            />
                        ))}
                    </div>
                </>
            )}
        </BottomSheet>
    );
}
